<template>
  <form @submit.stop.prevent class="ai-startup-form">
    <div>
      <h4>{{ $t("signupflow.step-3.ai.form.title") }}</h4>
      <p class="description text-muted text-small">
        {{ $t("signupflow.step-3.ai.form.description") }}
      </p>
    </div>

    <div class="selectors">
      <OutsideClickWrapper @click="languages = []" @focus="languages = []">
        <div class="language-select">
          <custom-input
            v-model="language"
            :placeholder="$t('signupflow.step-3.ai.form.select.placeholder')"
            :label="$t('signupflow.step-3.ai.form.select.label')"
            name="Production language"
            boxed
            @input="searchDebounce(language)"
            autoComplete="off"
          />
          <ul class="language-results" v-if="languages">
            <li class="language-result" v-for="(lang, index) in languages" :key="index">
              <button class="" @click.stop.prevent="onLanguageChange(lang)">{{ lang }}</button>
            </li>
          </ul>
        </div>
      </OutsideClickWrapper>
    </div>
    <button
      class="btn-signup-flow"
      type="button"
      @click="onPublish()"
      :disabled="!language || loadingWebhook || loadingTemplate"
    >
      <span>
        {{$t('signupflow.step-3.ai.form.primary-btn')}} <span v-show="loadingTemplate"><i class="fa-light fa-circle-notch fa-spin"></i></span>
      </span>
      <img src="@/assets/svg/arrow-right-ai.svg" alt="button arrow" />
    </button>

    <div class="ai-cta">
      <p class="title">{{$t('signupflow.step-3.ai.form.cta.title')}}</p>
      <p class="description">{{$t('signupflow.step-3.ai.form.cta.description')}}</p>
      <a :href="webHookLink" target="_blank" class="ai-link">{{$t('signupflow.step-3.ai.form.cta.link-text')}}</a>

      <div class="webhook-wrapper">
        <div class="input-wrapper">
          <div v-if="!webhookReady" class="overlay">
            <button type="button" @click="onPublish(true)" :disabled="!language || loadingWebhook || loadingTemplate">
              {{$t('signupflow.step-3.ai.form.cta.webhook-btn')}}
            </button>
            <span v-show="loadingWebhook"><i class="fa-light fa-circle-notch fa-spin"></i></span>
          </div>
          <custom-input
            v-model="webhook"
            boxed
            isDisabled
            inputType="text"
            name="Webhook"
            label="Webhook"
            id="webhookInput"
            :placeholder="$t('signupflow.step-3.ai.form.cta.placeholder')"
            :supportingText="webhookReady ? $t('signupflow.step-3.ai.form.cta.supporting-text-2') : $t('signupflow.step-3.ai.form.cta.supporting-text-1')"
          />
        </div>
        <button
          class="btn-copy"
          type="button"
          :disabled="!webhookReady"
          @click="copyWebhook"
          :tooltip="$t('signupflow.step-3.ai.form.cta.copy-tooltip')"
          tooltip-placement="left"
        >
          <i class="fa-light fa-clipboard"></i>
        </button>
      </div>
      <transition name="quick-fade-in">
        <div v-if="webhookReady" class="generation-settings">
          <p class="description">
            {{$t('signupflow.step-3.ai.form.webhook-ready.description')}}
            <a @click="navigateToPlatform()">{{$t('signupflow.step-3.ai.form.webhook-ready.navigation-text')}}</a>
            <!-- <router-link :to="'/?quickstart=true&templateType=ai&userCompletedFlow=true' ">the platform</router-link> -->
          </p>
          <a :href="webHookLink" target="_blank" class="ai-link">{{$t('signupflow.step-3.ai.form.webhook-ready.link-text')}}</a>
        </div>
      </transition>
    </div>
  </form>
</template>

<script>
import OutsideClickWrapper from "@/components/OutsideClickWrapper.vue";
import appHelper from "@/assets/js/appHelper";
import debounce from "lodash/debounce";
export default {
  components: {
    OutsideClickWrapper
  },
  data() {
    return {
      language: "",
      webhook: "some webhook",
      webhookReady: false,
      publishedId: null,
      templateCreated: false,
      template: null,
      loadingTemplate: false,
      loadingWebhook: false,
      languages: [],
      searchDebounce: debounce((filter) => {
        if (filter.length) this.getLanguages(filter);
        else {
          this.languages = [];
        }
      }, 100)
    };
  },
  props: {
    contentSource: {
      type: Object
    },
    sourceConnected: {
      type: Boolean,
      default: null
    }
  },
  methods: {
    getLanguages() {
      this.$store.dispatch("getLanguages", this.language).then((res) => {
        this.languages = res;
      });
    },
    findStandardTemplateId() {
      // Find the standard template that matches the content source
      if (this.isProduction) {
        switch (this.contentSource.contentType.name) {
          case "Shopify":
            return 1343;
          case "WooCommerce":
            return 1344;
          case "DanDomain":
            return 1345;
          case "Magento":
            return 1346;
          case "Shoporama":
            return 1347;
          default:
            return null;
        }
      } else {
        return 957;
      }
    },
    onPublish(requestedWebhook) {
      if (this.templateCreated) {
        this.navigateToEditor();
      } else this.publish(requestedWebhook);
    },
    publish(requestedWebhook) {
      // Create the template
      if (requestedWebhook) this.loadingWebhook = true;
      else this.loadingTemplate = true;
      this.$store
        .dispatch("createTemplate", {
          templateName: "My first AI template",
          contentType: this.contentSource.contentType,
          contentSource: [this.contentSource],
          language: this.language,
          standardTemplateId: this.findStandardTemplateId()
        })
        .then((res) => {
          this.template = { ...res };
          this.template.sections[0].outputSettings.language = this.language;
          this.templateCreated = true;

          this.$store.dispatch("saveTemplateFromSignupFlow", this.template).then((res) => {
            if (requestedWebhook) {
              const publication = {
                publicationName: "My first AI publication",
                template: { id: this.template.metadata.id },
                existingPublication: false
              };

              this.$store
                .dispatch("publishTemplate", publication)
                .then((res) => {
                  this.publishedId = res.id;
                  const apiKey = { name: "My api key", scopes: ["template_generate"] };
                  this.$store.dispatch("addApi", JSON.stringify(apiKey)).then((apiKeyResult) => {
                    this.webhook = `https://${!this.isProduction ? "staging." : ""}api.icrobotics.com/templates/${
                      this.publishedId
                    }/trigger?x-api-key=${apiKeyResult.key}`;
                    this.webhookReady = true;
                  });
                })
                .finally(() => {
                  this.loadingTemplate = false;
                  this.loadingWebhook = false;
                });
            } else this.navigateToEditor();
          });
        })
        .catch((err) => {
          this.loadingTemplate = false;
          this.loadingWebhook = false;
        });
      // this.webhookReady = true;
    },
    copyWebhook() {
      appHelper.copyToClipboard(this.webhook);
    },
    onLanguageChange(language) {
      this.language = language;
      this.languages = [];
    },
    navigateToEditor() {
      this.$store.dispatch("endSignupFlow", "AI").finally(() => {
        this.$router.push(`/templates/${this.template.metadata.id}?quickStart=true&templateType=ai&userCompletedFlow=true`);
      });
    },
    navigateToPlatform() {
      this.$store.dispatch("endSignupFlow", "AI").finally(() => {
        this.$router.push(`/?quickstart=true&templateType=ai&userCompletedFlow=true`);
      });
    }
  },
  computed: {
    isProduction() {
      return (
        document.location.hostname.includes("app.icrobotics.com") &&
        !document.location.hostname.includes("staging") &&
        !document.location.hostname.includes("dev")
      );
    },

    webHookLink() {
      switch (this.contentSource.contentType.name) {
        case "Shopify":
          return "https://help.icrobotics.com/how-to-generate-texts-from-ic-robotics-til-shopify";
        case "WooCommerce":
          return "https://help.icrobotics.com/how-do-i-generate-texts-from-ic-robotics-to-woocommerce";
        case "DanDomain":
          return "https://help.icrobotics.com/how-do-i-generate-texts-from-ic-robotics-to-dandomain-classic";
        case "Magento":
          return "https://help.icrobotics.com/how-do-i-generate-texts-for-magento2";
        case "Shoporama":
          return "https://help.icrobotics.com/integrations";
        default:
          return "https://help.icrobotics.com/integrations";
      }
    }
  }
};
</script>

<style lang="less">
#signup-flow main .ai-startup-form {
  .btn-signup-flow {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;

    &:disabled {
      background: none;
      color: var(--ai-secondary-40);
      border: 1px solid var(--ai-secondary-40);
      img {
        opacity: 0.4;
      }
    }
  }
}
.ai-cta {
  margin-top: 12px;
  .title {
    font-weight: 500;
    margin-bottom: 0px;
  }

  .webhook-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
    .input-wrapper {
      position: relative;
      flex-grow: 1;
      input {
        width: 100%;
      }
      .overlay {
        z-index: 10;
        position: absolute;
        background: #fff;
        left: 2px;
        bottom: 23px;
        padding: 2px 0;
        width: calc(100% - 4px);
        text-align: center;
        cursor: pointer;

        button {
          border: none;
          background: none;
          text-decoration: underline;
        }
      }
    }

    .btn-copy {
      border: none;
      background: none;
      margin-top: 16px;
    }
  }
  .generation-settings {
    margin-top: 18px;
  }
}

.language-select {
  position: relative;
  .language-results {
    position: absolute;
    top: calc(100% + 2px);
    left: 0;
    right: 0;
    background: #fff;
    z-index: 99;
    border-radius: 4px;
    box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.25);
    max-height: 200px;
    overflow-y: auto;
    padding: 0;
    list-style-type: none;
    display: flex;
    flex-direction: column;
    gap: 1px;
    .language-result {
      button {
        padding: 4px 12px;
        width: 100%;
        text-align: left;
        border: none;
        background: none;
        outline: none !important;
      }
      &:hover,
      &focus,
      &:focus-within {
        button {
          background: var(--charcoal-10);
          outline: none;
          border: none;
        }
      }
    }
  }
}

#app .ai-cta a {
  color: var(--ai-secondary);
  text-decoration: underline;
}
</style>
